import { AlertTriangle } from "lucide-react"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import type { SmithersDeviceConfig } from "@/types/smithers-device"

interface DeleteSmithersDeviceDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  devices: ReadonlyArray<Pick<SmithersDeviceConfig, "id" | "deviceId" | "clientId">>
  onConfirm: () => void
  isLoading: boolean
  error?: string
}

export function DeleteSmithersDeviceDialog({ open, onOpenChange, devices, onConfirm, isLoading, error }: DeleteSmithersDeviceDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={(next) => { if (!isLoading) onOpenChange(next) }}>
      <AlertDialogContent className="bg-card border-border max-w-[calc(100vw-2rem)] sm:max-w-xl">
        <AlertDialogHeader>
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-destructive/20 flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-destructive" />
            </div>
            <div className="min-w-0 flex-1">
              <AlertDialogTitle className="text-foreground">{devices.length > 1 ? `Delete ${devices.length} devices?` : "Delete device?"}</AlertDialogTitle>
              <AlertDialogDescription className="text-muted-foreground mt-2">
                This will permanently delete <span className="break-all font-mono text-foreground">{devices.length > 1 ? `${devices.length} devices` : devices[0]?.deviceId}</span>. Clients will be kept. This action cannot be undone.
              </AlertDialogDescription>
            </div>
          </div>
        </AlertDialogHeader>
        <div className="max-h-60 overflow-y-auto rounded-md border border-border" role="region" aria-label="Devices to delete">
          <Table className="table-fixed">
            <TableHeader><TableRow><TableHead>Device ID</TableHead><TableHead>Client ID</TableHead></TableRow></TableHeader>
            <TableBody>{devices.map((device) => (
              <TableRow key={device.id}>
                <TableCell className="whitespace-normal break-all font-mono">{device.deviceId}</TableCell>
                <TableCell className="whitespace-normal break-all font-mono">{device.clientId}</TableCell>
              </TableRow>
            ))}</TableBody>
          </Table>
        </div>
        {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
        <AlertDialogFooter className="mt-2">
          <AlertDialogCancel disabled={isLoading} className="bg-secondary border-input text-muted-foreground hover:bg-secondary/80 hover:text-foreground">Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={isLoading}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={(event) => { event.preventDefault(); onConfirm() }}
          >
            {isLoading ? "Deleting…" : "Delete"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
